/**
 * Contact lens PLP — LensDirect popularity order.
 * Merges all collection pages, then reorders for all / brand / lens-type filters.
 */
(function () {
  const ORDER_ID = 'ciw-contacts-ld-order';
  const ORDERS_DATA_ID = 'ciw-contacts-ld-orders-data';
  const GRID_SELECTOR = '#product-grid';
  const CARD_SELECTOR = '.ciw-card';
  const WRAP_SELECTOR = '.ciw-product-grid-wrap';
  const LOADED_COUNT_SELECTOR = '[data-view-more-loaded]';
  const COUNT_DESKTOP_SELECTOR = '#CiwProductCount';
  const COUNT_MOBILE_SELECTOR = '#CiwProductCountMobile';
  const BRAND_INPUT_SELECTOR = '[data-ciw-contacts-brand-input]:checked';
  const LENS_TYPE_INPUT_SELECTOR = '[data-ciw-contacts-lens-type-input]:checked';
  const MAX_MERGE_PAGES = 5;
  const BAUSCH_LOMB_LINE_BRANDS = new Set(['bausch-lomb', 'ultra', 'biotrue', 'soflens', 'purevision']);

  let mergeGeneration = 0;
  let ordersCache = null;
  let ordersPromise = null;
  let pageMergeComplete = false;
  let mergePromise = null;

  function getContactsPath() {
    if (window.ciwContactsCollectionPath) return window.ciwContactsCollectionPath;
    const match = window.location.pathname.match(/^(\/collections\/[^/?#]+)/);
    return match ? match[1] : '/collections/contacts';
  }

  function getSectionId() {
    return document.querySelector(GRID_SELECTOR)?.dataset.id || window.ciwContactsSectionId || null;
  }

  function isPriceOrNameSort() {
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    return (
      sort === 'price-ascending' ||
      sort === 'price-descending' ||
      sort === 'title-ascending' ||
      sort === 'title-descending'
    );
  }

  function isContactsLdGrid() {
    const grid = document.querySelector(GRID_SELECTOR);
    return Boolean(grid && grid.classList.contains('ciw-product-grid--ld-sort'));
  }

  function getSelectedBrandSlugs() {
    if (typeof window.ciwGetSelectedContactBrandSlugs === 'function') {
      return window.ciwGetSelectedContactBrandSlugs();
    }

    const slugs = [];
    const seen = new Set();
    document.querySelectorAll(BRAND_INPUT_SELECTOR).forEach((input) => {
      if (seen.has(input.value)) return;
      seen.add(input.value);
      slugs.push(input.value);
    });
    return slugs;
  }

  function getSelectedLensTypeSlugs() {
    const slugs = [];
    const seen = new Set();
    document.querySelectorAll(LENS_TYPE_INPUT_SELECTOR).forEach((input) => {
      if (seen.has(input.value)) return;
      seen.add(input.value);
      slugs.push(input.value);
    });
    return slugs;
  }

  function readLegacyOrder() {
    const raw = document.getElementById(ORDER_ID)?.textContent?.trim();
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((h) => String(h).trim()).filter(Boolean) : [];
  }

  async function loadOrders() {
    if (ordersCache) return ordersCache;

    if (!ordersPromise) {
      ordersPromise = (async () => {
        const dataEl = document.getElementById(ORDERS_DATA_ID);
        if (dataEl?.dataset.src) {
          try {
            const response = await fetch(dataEl.dataset.src);
            if (response.ok) {
              ordersCache = await response.json();
              return ordersCache;
            }
          } catch (error) {
            /* fall back */
          }
        }

        ordersCache = { all: readLegacyOrder(), brands: {}, lensTypes: {} };
        return ordersCache;
      })();
    }

    return ordersPromise;
  }

  function isBauschLombHandle(handle) {
    if (!handle) return false;
    return /^(bausch-lomb-ultra|biotrue|infuse|purevision|soflens)/.test(handle);
  }

  function isBauschLombFamilyCard(card) {
    const brand =
      card?.getAttribute?.('data-ciw-contact-brand') || card?.dataset?.ciwContactBrand || '';
    const handle = card?.getAttribute?.('data-handle') || card?.dataset?.handle || '';
    if (card?.classList?.contains('ciw-card--bausch-lomb-family')) return true;
    if (
      card?.getAttribute?.('data-ciw-contact-brand-family') === 'bausch-lomb' ||
      card?.dataset?.ciwContactBrandFamily === 'bausch-lomb'
    ) {
      return true;
    }
    if (isBauschLombHandle(handle)) return true;
    return BAUSCH_LOMB_LINE_BRANDS.has(brand);
  }

  function isBiotrueHandle(handle) {
    if (!handle) return false;
    return /^biotrue/.test(handle);
  }

  function cardMatchesSelectedBrandSlugs(card, brandSlugs) {
    const brand =
      card.getAttribute('data-ciw-contact-brand') || card.dataset.ciwContactBrand || '';
    const handle = card.getAttribute('data-handle') || card.dataset.handle || '';
    if (brand !== '' && brandSlugs.includes(brand)) return true;
    if (brandSlugs.includes('bausch-lomb') && isBauschLombFamilyCard(card)) return true;
    if (brandSlugs.includes('biotrue') && isBiotrueHandle(handle)) return true;
    return false;
  }

  function hydrateMergedContactCards(grid) {
    if (!grid) return;
    grid.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (typeof window.ciwHydrateContactCardAttributes === 'function') {
        window.ciwHydrateContactCardAttributes(card);
      }
    });
  }

  function getBrandCardKeysForSlugs(brandSlugs, orders) {
    const keys = new Set();
    if (!brandSlugs.length) return keys;

    const cardMap = cardsFromDom();

    cardMap.forEach((card, key) => {
      if (cardMatchesSelectedBrandSlugs(card, brandSlugs)) keys.add(key);
    });

    if (orders?.brands) {
      brandSlugs.forEach((slug) => {
        (orders.brands[slug] || []).forEach((catalogKey) => keys.add(catalogKey));
      });
    }

    return keys;
  }

  function buildBrandFilteredOrder(brandSlugs, orders, all) {
    const brandKeys = getBrandCardKeysForSlugs(brandSlugs, orders);
    if (!brandKeys.size) return null;

    if (brandSlugs.length === 1) {
      const brandOrder = orders?.brands?.[brandSlugs[0]];
      if (brandOrder?.length) {
        const ordered = [];
        brandOrder.forEach((key) => {
          if (brandKeys.has(key)) ordered.push(key);
        });
        brandKeys.forEach((key) => {
          if (!ordered.includes(key)) ordered.push(key);
        });
        return ordered;
      }
    }

    return all.filter((key) => brandKeys.has(key));
  }

  function resolveActiveOrder(orders) {
    const all = orders?.all?.length ? orders.all : readLegacyOrder();
    const brandSlugs = getSelectedBrandSlugs();
    const lensSlugs = getSelectedLensTypeSlugs();

    if (brandSlugs.length >= 1) {
      const brandOrder = buildBrandFilteredOrder(brandSlugs, orders, all);
      if (brandOrder?.length) return brandOrder;
    }

    if (lensSlugs.length === 1) {
      const lensOrder = orders?.lensTypes?.[lensSlugs[0]];
      if (lensOrder?.length) return lensOrder;
    }

    return all;
  }

  function cardSortKey(card) {
    return (
      card.getAttribute('data-ciw-card-key') ||
      card.dataset.ciwCardKey ||
      card.getAttribute('data-handle') ||
      card.dataset.handle ||
      ''
    );
  }

  function isContactCard(card) {
    return (
      card.classList.contains('ciw-card--contact-lens') ||
      Boolean(card.dataset.ciwContactBrand) ||
      Boolean(card.dataset.ciwContactLensTypes)
    );
  }

  function addCardsToMap(map, cards) {
    let added = 0;
    cards.forEach((card) => {
      if (!isContactCard(card)) return;
      const key = cardSortKey(card);
      if (!key || map.has(key)) return;
      map.set(key, card);
      added += 1;
    });
    return added;
  }

  function cardsFromDoc(doc) {
    const map = new Map();
    doc.querySelectorAll(`${GRID_SELECTOR} ${CARD_SELECTOR}`).forEach((card) => {
      if (!isContactCard(card)) return;
      const key = cardSortKey(card);
      if (key && !map.has(key)) map.set(key, card);
    });
    return map;
  }

  function cardsFromDom() {
    const grid = document.querySelector(GRID_SELECTOR);
    const map = new Map();
    if (!grid) return map;
    grid.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      if (!isContactCard(card)) return;
      const key = cardSortKey(card);
      if (key && !map.has(key)) map.set(key, card);
    });
    return map;
  }

  function getCardForOrderKey(cardMap, orderKey) {
    if (cardMap.has(orderKey)) return cardMap.get(orderKey);
    return null;
  }

  function reorderGrid(order) {
    const grid = document.querySelector(GRID_SELECTOR);
    if (!grid || !order.length) return;

    const cardMap = cardsFromDom();
    const used = new Set();

    order.forEach((key) => {
      const card = getCardForOrderKey(cardMap, key);
      if (!card || used.has(card)) return;
      grid.appendChild(card);
      used.add(card);
    });

    cardMap.forEach((card) => {
      if (used.has(card)) return;
      grid.appendChild(card);
    });

    syncContactPackCardLinks();
  }

  function buildContactPackCardUrl(card) {
    const variantId = card.getAttribute('data-ciw-variant-id');
    const pack = card.getAttribute('data-ciw-cl-pack');
    const handle = card.getAttribute('data-handle');
    if (!variantId || !pack || !handle) return null;
    const packParam = encodeURIComponent(pack);
    return `/products/${handle}?variant=${variantId}&ciw_pack=${packParam}`;
  }

  function syncContactPackCardLinks(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('.ciw-card--contact-lens-pack[data-ciw-variant-id]').forEach((card) => {
      const href = buildContactPackCardUrl(card);
      if (!href) return;
      card.querySelectorAll('a.ciw-card__image, a.ciw-card__info').forEach((link) => {
        link.setAttribute('href', href);
      });
    });
  }

  window.ciwSyncContactPackCardLinks = syncContactPackCardLinks;

  function applyContactsGridOrderSync(orders) {
    if (!isContactsLdGrid() || isPriceOrNameSort()) return;
    reorderGrid(resolveActiveOrder(orders));
  }

  async function applyContactsGridOrder() {
    if (!isContactsLdGrid() || isPriceOrNameSort()) return;
    const orders = await loadOrders();
    applyContactsGridOrderSync(orders);
  }

  async function ensureMergeComplete() {
    if (pageMergeComplete) return;
    if (!mergePromise) {
      mergePromise = mergeAllContactPages().finally(() => {
        mergePromise = null;
      });
    }
    await mergePromise;
  }

  async function refreshContactsPlpGrid() {
    if (!isContactsLdGrid() || isPriceOrNameSort()) {
      if (typeof window.ciwApplyContactsPlpFilterVisibility === 'function') {
        await window.ciwApplyContactsPlpFilterVisibility();
      }
      return;
    }

    await ensureMergeComplete();

    const orders = await loadOrders();
    applyContactsGridOrderSync(orders);

    if (typeof window.ciwApplyContactsPlpFilterVisibility === 'function') {
      await window.ciwApplyContactsPlpFilterVisibility();
    }

    if (typeof window.ciwUpdateContactsViewMoreVisibility === 'function') {
      window.ciwUpdateContactsViewMoreVisibility();
    }
  }

  window.ciwApplyContactsGridOrder = applyContactsGridOrder;
  window.ciwRefreshContactsPlpGrid = refreshContactsPlpGrid;
  window.ciwResetContactsPlpMerge = resetMergeState;

  function buildSectionPageUrl(page) {
    const sectionId = getSectionId();
    if (!sectionId) return null;
    const params = new URLSearchParams();
    params.set('section_id', sectionId);
    params.set('filter.v.availability', '1');
    const sort = new URLSearchParams(window.location.search).get('sort_by');
    if (sort) params.set('sort_by', sort);
    const previewThemeId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewThemeId) params.set('preview_theme_id', previewThemeId);
    if (page > 1) params.set('page', String(page));
    return `${getContactsPath()}?${params.toString()}`;
  }

  function resetMergeState() {
    pageMergeComplete = false;
    mergePromise = null;
    mergeGeneration += 1;
  }

  function startPagePrefetch() {
    const url = buildSectionPageUrl(2);
    if (!url || window.__ciwContactsPage2Prefetch) return;
    window.__ciwContactsPage2Prefetch = fetch(url);
  }

  async function fetchSectionPageCards(page) {
    const url = buildSectionPageUrl(page);
    if (!url) return new Map();

    async function loadFromResponse(response) {
      if (!response?.ok) return new Map();
      const html = await response.text();
      return cardsFromDoc(new DOMParser().parseFromString(html, 'text/html'));
    }

    if (page === 2 && window.__ciwContactsPage2Prefetch) {
      const prefetch = window.__ciwContactsPage2Prefetch;
      window.__ciwContactsPage2Prefetch = null;
      const prefetched = await loadFromResponse(await prefetch);
      if (prefetched.size) return prefetched;
    }

    return loadFromResponse(await fetch(url));
  }

  function shouldMergeAllPages() {
    if (pageMergeComplete) return false;
    if (!isContactsLdGrid()) return false;
    if (!document.getElementById(ORDER_ID)) return false;
    if (isPriceOrNameSort()) return false;
    return true;
  }

  function finishSort(wrap) {
    if (wrap) wrap.classList.add('is-ld-sorted');
  }

  async function mergeAllContactPages() {
    if (!shouldMergeAllPages()) return;

    if (typeof window.ciwStripContactsServerFilters === 'function') {
      window.ciwStripContactsServerFilters();
    }

    const generation = ++mergeGeneration;
    const grid = document.querySelector(GRID_SELECTOR);
    const wrap = document.querySelector(WRAP_SELECTOR);
    const orders = await loadOrders();
    const mergeOrder = orders?.all?.length ? orders.all : readLegacyOrder();

    if (!grid || !mergeOrder.length) {
      finishSort(wrap);
      return;
    }

    try {
      const cardMap = new Map();

      for (let page = 1; page <= MAX_MERGE_PAGES; page += 1) {
        if (generation !== mergeGeneration) return;
        const pageCards = await fetchSectionPageCards(page);
        if (!pageCards.size) break;

        addCardsToMap(cardMap, pageCards);
      }

      if (generation !== mergeGeneration) return;

      if (!cardMap.size) {
        finishSort(wrap);
        return;
      }

      const fragment = document.createDocumentFragment();
      const used = new Set();

      mergeOrder.forEach((key) => {
        const card = getCardForOrderKey(cardMap, key);
        if (!card) return;
        const cardKey = cardSortKey(card);
        if (used.has(cardKey)) return;
        fragment.appendChild(card.cloneNode(true));
        used.add(cardKey);
      });

      cardMap.forEach((card, key) => {
        if (used.has(key)) return;
        fragment.appendChild(card.cloneNode(true));
      });

      grid.replaceChildren(fragment);
      pageMergeComplete = true;
      hydrateMergedContactCards(grid);
      syncContactPackCardLinks();

      if (typeof window.ciwApplyContactsPlpFilterVisibility === 'function') {
        await window.ciwApplyContactsPlpFilterVisibility();
      }

      if (typeof window.ciwUpdateContactsViewMoreVisibility === 'function') {
        window.ciwUpdateContactsViewMoreVisibility();
      } else {
        const viewMoreWrap = document.querySelector('.ciw-view-more-wrap');
        if (viewMoreWrap) viewMoreWrap.hidden = true;
      }

      finishSort(wrap);
    } catch (error) {
      if (generation === mergeGeneration) finishSort(wrap);
    }
  }

  function waitForContactsFiltersReady() {
    if (!document.querySelector('[data-ciw-contacts-brand-filter]')) return Promise.resolve();
    if (window.ciwContactsFiltersReady) return Promise.resolve();
    return new Promise((resolve) => {
      document.addEventListener('ciw:contacts-filters-ready', resolve, { once: true });
      setTimeout(resolve, 500);
    });
  }

  async function afterGridUpdated() {
    await ensureMergeComplete();
    await refreshContactsPlpGrid();
  }

  document.addEventListener('ciw:collection-updated', () => {
    resetMergeState();
    window.requestAnimationFrame(() => {
      afterGridUpdated();
    });
  });

  loadOrders().then(() => {
    waitForContactsFiltersReady().then(() => {
      ensureMergeComplete().then(refreshContactsPlpGrid);
    });
  });
  startPagePrefetch();

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest('#product-grid a.ciw-card__image, #product-grid a.ciw-card__info');
      if (!link) return;
      const card = link.closest('.ciw-card--contact-lens-pack[data-ciw-variant-id]');
      if (!card) return;
      const href = buildContactPackCardUrl(card);
      if (!href) return;
      if ((link.getAttribute('href') || '') === href) return;
      event.preventDefault();
      window.location.assign(href);
    },
    true
  );
})();
